import { useAppSelector } from './redux';

/**
 * Hook for role-based access control
 * Returns utilities to check user roles and control UI visibility
 */
export const useRoleAccess = () => {
  const { mongoUser } = useAppSelector((state) => state.auth);

  const isAdmin = mongoUser?.role === 'admin';
  const isUser = mongoUser?.role === 'user';
  const isSuperAdmin = mongoUser?.role === 'super_admin';
  const userRole = mongoUser?.role || null;
  /** The viewer's Firebase uid: what a session's `userId` holds, so it decides ownership. */
  const currentUserUid = mongoUser?.firebaseUid ?? null;

  /**
   * Check if user has admin role
   */
  const hasAdminAccess = (): boolean => {
    return isAdmin;
  };

  /**
   * Check if user has access to a specific feature/route.
   * Super admins are treated as admins for role checks — their route
   * restriction to knowledge-base-only is enforced separately via isSuperAdmin.
   * @param requiredRole - 'admin' | 'user' | 'any'
   */
  const hasAccess = (requiredRole: 'admin' | 'user' | 'any'): boolean => {
    if (requiredRole === 'any') return true;
    if (requiredRole === 'admin') return isAdmin || isSuperAdmin;
    if (requiredRole === 'user') return isUser || isAdmin || isSuperAdmin;
    return false;
  };

  /**
   * Check if a route/feature should be visible based on role
   * @param requiredRole - 'admin' | 'user' | 'any'
   */
  const canView = (requiredRole: 'admin' | 'user' | 'any'): boolean => {
    return hasAccess(requiredRole);
  };

  return {
    isAdmin,
    isUser,
    isSuperAdmin,
    userRole,
    currentUserUid,
    hasAdminAccess,
    hasAccess,
    canView,
  };
};
